import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import Button from 'components/field/Button';
import MapField from 'components/field/MapField';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Collapse from 'components/global/Collapse';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { OPEN } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCircleCheck, faCircleX, faHouseCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { faAngleDown, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';

import * as S from './styled/BulletConfirmation.styled';

const BulletConfirmation = (props) => {
	const {
		address,
		otoOrderID,
		orderNumber,
		orderDate,
		receiverName,
		availableTimeSlots,
		errorCode,
		bulletTransactionId,
		senderName,
		cutOffTime
	} = props;
	const [loading, setLoading] = useState(null);
	const { t } = useTranslation();

	const { showError } = useAlert();

	const [successfullyConfirmedBullet, setSuccessfullyConfirmedBullet] = useState(false);
	const [successfulConfirmationText, setSuccessfulConfirmationText] = useState(null);
	const [addressConditionIsNotSatisfied, setAddressConditionIsNotSatisfied] = useState(false);
	const [confirmationError, setConfirmationError] = useState(false);
	const [confirmationFalureDescription, setConfirmationFalureDescription] = useState(null);
	const [timeSlots, setTimeSlots] = useState(availableTimeSlots);

	const axios = useAxiosPrivate();
	const mapRef = useRef(null);
	const orderInfoAccordionRef = useRef(null);

	const schema = useMemo(
		() =>
			yup.object().shape({
				confirmedAddress: yup.object().shape({
					text: yup.string().nullable().required(),
					countryName: yup.string().nullable(),
					stateName: yup.string().nullable(),
					cityName: yup.string().nullable(),
					districtName: yup.string().nullable(),
					zipCode: yup.string().nullable(),
					lat: yup.number().min(-90).max(90).nullable(),
					lng: yup.number().min(-180).max(180).nullable(),
					buildingName: yup.string().nullable(),
					street: yup.string().nullable()
				}),
				isAvailableToday: yup.boolean().required(),
				timeSlot: yup
					.object()
					.nullable()
					.shape({
						start: yup.string().required(),
						end: yup.string().required()
					})
					.when('isAvailableToday', {
						is: true,
						then: (schema) => schema.required()
					}),
				deliveryNote: yup.string().nullable().max(160)
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			isAvailableToday: null,
			timeSlot: null,
			deliveryNote: null,
			confirmedAddress: null
		}
	});

	const { getLocaleDateWithTime, getTimeWithAMPM } = useLocale();

	const { setValue, watch, handleSubmit, reset } = formMethods;

	useEffect(() => {
		if (errorCode === 'SMS010') {
			setConfirmationError(true);
			setConfirmationFalureDescription('confirmationNoLongerAvailable');
		} else if (errorCode === 'SMS006') {
			setConfirmationError(true);
			setConfirmationFalureDescription('allDeliverySlotsAreExpiredClientWillProvideAnotherSlot');
		} else {
			reset({
				confirmedAddress: {
					text: address?.text,
					lat: address?.latitude,
					lng: address?.longitude,
					zipCode: address?.postcode,
					countryName: address?.countryName,
					stateName: address?.stateName,
					cityName: address?.cityName,
					districtName: address?.districtName,
					buildingName: address?.buildingName,
					street: address?.street
				}
			});
		}
	}, [address, errorCode]);

	const afterConfirmation = async (data) => {
		setLoading(true);
		try {
			const response = await axios.post(OPEN.SMS.CONFIRM_BULLET_DELIVERY, {
				otoOrderID: props.otoOrderID,
				availableToday: data.isAvailableToday,
				selectedSlot: data.timeSlot,
				deliveryNote: data.deliveryNote,
				bulletTransactionId
			});

			if (response.status === 200) {
				if (data.isAvailableToday) {
					setSuccessfulConfirmationText('orderWillBeDeliveredToYourAddress');
				}
				if (!data.isAvailableToday) {
					setSuccessfulConfirmationText('weUndrstandYouAreNotAvailableToday');
				}
				setSuccessfullyConfirmedBullet(true);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			showError({ message: error.response?.data?.code });
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	const onSubmit = (data) => {
		if (data.isAvailableToday) {
			NiceModal.show(ConfirmationModal, {
				title: 'confirmAvalabilityWithSelectedAddressAndTimeTitle',
				subTitle: (
					<Trans
						i18nKey="confirmAvalabilityWithSelectedAddressAndTimeDescription"
						values={{
							orderNumber,
							date: dayjs().format('dddd, MMMM D, YYYY'),
							slotStart: data.timeSlot?.start,
							slotEnd: data.timeSlot?.end,
							address: data.confirmedAddress.text
						}}
					/>
				),
				yesAction: () => afterConfirmation(data),
				showCloseButton: true
			});
		} else {
			NiceModal.show(ConfirmationModal, {
				title: 'confirmNotAvailableTitle',
				subTitle: (
					<Trans
						i18nKey="confirmNotAvailableDescription"
						values={{
							client: senderName
						}}
					/>
				),
				yesAction: () => afterConfirmation(data),
				showCloseButton: true
			});
		}
	};

	if (confirmationError) {
		return <Banner status="error" bannerPageContentText={confirmationFalureDescription} client={senderName} />;
	}

	if (successfullyConfirmedBullet) {
		return <Banner status="success" bannerPageContentText={successfulConfirmationText} client={senderName} />;
	}

	if (addressConditionIsNotSatisfied) {
		return <Banner status="error" bannerPageContentText={'addressConditionIsNotSatisfied'} client={senderName} />;
	}

	return (
		<S.RoundedCard shadowed>
			<FormProvider {...formMethods}>
				<S.AccordionHeader ref={orderInfoAccordionRef} className="accordion-header">
					<S.HeaderAndActionWrapper>
						<Typography gutterBottom fontWeight="700">
							orderInformation
						</Typography>
						<Icon className="expand-icon" icon={faAngleDown} size="medium" />
					</S.HeaderAndActionWrapper>
					<Alert
						iconUrl="https://storage.googleapis.com/tryoto-public/public/assets/icons/availableDeliveryHours.svg"
						content={
							<Trans
								i18nKey="clientIsCheckingYourAvailability"
								values={{
									client: senderName,
									cutOffTime
								}}
							/>
						}
					/>
				</S.AccordionHeader>

				<Collapse buttonRef={orderInfoAccordionRef} defaultVisible>
					<S.OrderInformationContentWrapper>
						<div>
							<Typography gutterBottom>orderID</Typography>
							<Typography fontWeight="700">{orderNumber}</Typography>
						</div>
						<div>
							<Typography gutterBottom>senderName</Typography>
							<Typography fontWeight="700">{senderName}</Typography>
						</div>

						<div>
							<Typography gutterBottom>receiverName</Typography>
							<Typography fontWeight="700">{receiverName}</Typography>
						</div>
						<div>
							<Typography gutterBottom>orderDate</Typography>
							<S.OrderDateWrapper>
								<Typography fontWeight="700">{getLocaleDateWithTime(orderDate)}</Typography>
								<Typography fontWeight="700">{getTimeWithAMPM(orderDate)}</Typography>
							</S.OrderDateWrapper>
						</div>
					</S.OrderInformationContentWrapper>
				</Collapse>

				<S.Divider />
				<Typography fontWeight="700" gutterBottom>
					yourAddress
				</Typography>

				<Typography noWrap gutterBottom>
					{watch('confirmedAddress')?.text}
				</Typography>
				<S.MapWrapper>
					<MapField
						ref={mapRef}
						value={{
							lat: watch('confirmedAddress')?.lat ?? 0,
							lng: watch('confirmedAddress')?.lng ?? 0
						}}
						zoom={17}
						disabled
						showAutocomplete={false}
					/>
				</S.MapWrapper>
				<Button
					icon={faLocationDot}
					fullWidth
					label="changeMyAddress"
					variant="outline"
					onClick={() =>
						NiceModal.show(ChangeAddressModal, {
							otoOrderID,
							address: watch('confirmedAddress'),
							orderId: orderNumber,
							client: senderName,
							setAddressConditionIsNotSatisfied,
							setNewAddress: (newAddress) => {
								setValue('confirmedAddress', newAddress, {
									shouldDirty: true
								});
							},
							bulletTransactionId,
							setNewSlots: (newSlots) => {
								setTimeSlots(newSlots);
							}
						})
					}
				/>
				<S.Divider />
				<Typography fontWeight="700" gutterBottom>
					selectADeliveryOption
				</Typography>

				<S.DeliveryOptionsWrapper>
					<S.LargeOutlinedButton
						variant={watch('isAvailableToday') && 'selected'}
						onClick={() => {
							setValue('isAvailableToday', true, { shouldDirty: true });
						}}>
						<Icon size={'large'} icon={faHouseCircleCheck} />
						{t('imAvalableToday')}
					</S.LargeOutlinedButton>
					<S.LargeOutlinedButton
						variant={watch('isAvailableToday') === false && 'selected'}
						onClick={() => {
							setValue('isAvailableToday', false, { shouldDirty: true });
							setValue('timeSlot', null, { shouldDirty: true });
						}}>
						<Icon size={'large'} kitName="fa-light-house-slash" />
						{t('imNotAvalableToday')}
					</S.LargeOutlinedButton>
				</S.DeliveryOptionsWrapper>

				{watch('isAvailableToday') && (
					<S.TimeSlotsWrapper>
						<Typography fontWeight="700" gutterBottom>
							selectTimeSlot
						</Typography>
						<Typography variant="caption" color="var(--content-3)" gutterBottom>
							timeSlot
						</Typography>
						<FormItem name="timeSlot">
							<S.TimeSlots>
								{timeSlots.map((slot, index) => (
									<S.SmallOutlinedButton
										key={index}
										variant={
											slot.start === watch('timeSlot')?.start &&
											slot.end === watch('timeSlot')?.end &&
											'selected'
										}
										onClick={() => {
											setValue('timeSlot', slot, { shouldDirty: true });
										}}>
										{slot.start} - {slot.end}
									</S.SmallOutlinedButton>
								))}
							</S.TimeSlots>
							{formMethods.formState.errors.timeSlot && (
								<Typography variant="caption" color="var(--red)" gutterBottom>
									{formMethods.formState.errors.timeSlot?.start?.message ||
										formMethods.formState.errors.timeSlot?.end?.message ||
										formMethods.formState.errors.timeSlot?.message}
								</Typography>
							)}
						</FormItem>
					</S.TimeSlotsWrapper>
				)}

				<S.DeliveryNoteWrapper>
					<Typography fontWeight="700">
						{t('deliveryNote')}
						<Tooltip placement="right" title={'deliveryNoteToolpit'}>
							<Icon icon={faCircleInfo} className="mx-2" />
						</Tooltip>
					</Typography>
					<FormItem name="deliveryNote">
						<S.NotResizableTextArea rows="6" placeholder="deliveryNotePlaceholder" name="deliveryNote" />
						<S.CharacterCounter
							variant="caption"
							color={
								watch('deliveryNote')?.length / 160 <= 1 || !watch('deliveryNote')
									? 'var(--content-3)'
									: 'var(--red)'
							}>
							{watch('deliveryNote')?.length ?? 0}/160
						</S.CharacterCounter>
					</FormItem>
				</S.DeliveryNoteWrapper>

				<Button
					fullWidth
					label="confirm"
					variant="blue"
					size="large"
					disabled={
						(watch('isAvailableToday') === null || watch('isAvailableToday') === undefined) &&
						!watch('timeSlot')
					}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					loading={loading}
				/>
			</FormProvider>
		</S.RoundedCard>
	);
};

const Banner = ({ status, bannerPageContentText, client }) => (
	<S.FullPageBanner>
		<S.Banner>
			<S.LargeIcon
				icon={status === 'success' ? faCircleCheck : faCircleX}
				color={status === 'success' ? 'var(--green)' : 'var(--red)'}
			/>
			<Typography variant="p1" noWrap fontWeight="700" align="center" gutterBottom>
				<Trans i18nKey={bannerPageContentText} values={{ client }} />
			</Typography>
		</S.Banner>
	</S.FullPageBanner>
);

const ChangeAddressModal = NiceModal.create(
	({
		otoOrderID,
		address,
		client,
		orderId,
		setNewAddress,
		setNewSlots,
		setAddressConditionIsNotSatisfied,
		bulletTransactionId
	}) => {
		const modal = useModal();
		const [loading, setLoading] = useState(null);
		const axios = useAxiosPrivate();
		const { showError } = useAlert();

		const schema = useMemo(
			() =>
				yup.object().shape({
					newAddress: yup.object().shape({
						text: yup.string().nullable().required(),
						countryName: yup.string().nullable(),
						stateName: yup.string().nullable(),
						cityName: yup.string().nullable(),
						districtName: yup.string().nullable(),
						zipCode: yup.string().nullable(),
						lat: yup.number().min(-90).max(90).nullable(),
						lng: yup.number().min(-180).max(180).nullable(),
						buildingName: yup.string().nullable(),
						street: yup.string().nullable(),
						addressInputType: yup.string().nullable().oneOf(['fillFromGoogleMaps'])
					})
				}),
			[]
		);

		const formMethods = useForm({
			resolver: yupResolver(schema)
		});

		const {
			handleSubmit,
			watch,
			reset,
			formState: { isDirty, isSubmitSuccessful, isSubmitting }
		} = formMethods;

		useEffect(() => {
			reset({
				newAddress: {
					text: address?.text,
					lat: address?.lat,
					lng: address?.lng,
					zipCode: address?.zipCode,
					countryName: address?.countryName,
					stateName: address?.stateName,
					cityName: address?.cityName,
					districtName: address?.districtName,
					buildingName: address?.buildingName,
					street: address?.street,
					addressInputType: address?.addressInputType ?? 'fillFromGoogleMaps'
				}
			});
		}, [address]);

		const outOfBoundAddressConfirmation = async () => {
			setLoading(true);
			try {
				const response = await axios.post(OPEN.SMS.UPDATE_BULLET_CONFIRMATION_ADDRESS, {
					otoOrderID,
					address: {
						text: watch('newAddress').text,
						lat: watch('newAddress').lat,
						lng: watch('newAddress').lng,
						postcode: watch('newAddress').zipCode,
						countryName: watch('newAddress').countryName,
						stateName: watch('newAddress').stateName,
						cityName: watch('newAddress').cityName,
						districtName: watch('newAddress').districtName,
						buildingName: watch('newAddress').buildingName,
						street: watch('newAddress').street
					},
					bulletTransactionId,
					approved: true
				});

				if (response.status === 200) {
					setNewAddress(watch('newAddress'));
					setAddressConditionIsNotSatisfied(true);
					modal.remove();
				} else {
					showError({ message: response });
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(error);
				showError();
			} finally {
				setLoading(false);
			}
		};

		const onSubmit = async (data) => {
			setLoading(true);
			try {
				const response = await axios.post(OPEN.SMS.UPDATE_BULLET_CONFIRMATION_ADDRESS, {
					otoOrderID,
					address: {
						text: data.newAddress.text,
						lat: data.newAddress.lat,
						lng: data.newAddress.lng,
						postcode: data.newAddress.zipCode,
						countryName: data.newAddress.countryName,
						stateName: data.newAddress.stateName,
						cityName: data.newAddress.cityName,
						districtName: data.newAddress.districtName,
						buildingName: data.newAddress.buildingName,
						street: data.newAddress.street
					},
					bulletTransactionId
				});

				if (response.status === 200) {
					if (!response.data.errorCode && !response.data.errorMsg) {
						if (response.data?.newDeliverySlotList) {
							setNewSlots(response.data.newDeliverySlotList);
						}
						setNewAddress(watch('newAddress'));
						modal.remove();
					} else if (response.data.errorCode === '1') {
						NiceModal.show(ConfirmationModal, {
							title: (
								<Trans
									i18nKey="outOfCoverageAreaTitle"
									values={{
										client
									}}
								/>
							),
							subTitle: (
								<Trans
									i18nKey="outOfCoverageAreaSubtitle"
									values={{
										client,
										orderId
									}}
								/>
							),
							rejectLabel: 'cancel',
							confirmLabel: 'confirmNewAddress',
							lottieName: 'info-animated-icon',
							yesAction: () => outOfBoundAddressConfirmation(),
							showCloseButton: true
						});
					} else if (response.data.errorCode === '2') {
						NiceModal.show(ConfirmationModal, {
							title: (
								<Trans
									i18nKey="priceHigherThanAllowedTitle"
									values={{
										client
									}}
								/>
							),
							subTitle: (
								<Trans
									i18nKey="priceHigherThanAllowedSubtitle"
									values={{
										client,
										orderId
									}}
								/>
							),
							rejectLabel: 'cancel',
							confirmLabel: 'confirmNewAddress',
							lottieName: 'info-animated-icon',
							yesAction: () => outOfBoundAddressConfirmation(),
							showCloseButton: true
						});
					} else if (response.data.errorCode === '3') {
						NiceModal.show(ConfirmationModal, {
							title: 'newDistanceHigherThanMaxTitle',
							subTitle: (
								<Trans
									i18nKey="newDistanceHigherThanMaxSubtitle"
									values={{
										client,
										orderId
									}}
								/>
							),
							rejectLabel: 'cancel',
							confirmLabel: 'confirmNewAddress',
							lottieName: 'info-animated-icon',
							yesAction: () => outOfBoundAddressConfirmation(),
							showCloseButton: true
						});
					} else if (response.data.errorCode === '4') {
						NiceModal.show(ConfirmationModal, {
							title: 'noAvailabeDeliverySlotTitle',
							subTitle: (
								<Trans
									i18nKey="noAvailabeDeliverySlotSubtitle"
									values={{
										client,
										orderId
									}}
								/>
							),
							rejectLabel: 'cancel',
							confirmLabel: 'confirmNewAddress',
							lottieName: 'info-animated-icon',
							yesAction: () => outOfBoundAddressConfirmation(),
							showCloseButton: true
						});
					}
				} else {
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(error);
				showError({
					message: error.response?.data?.code
				});
			} finally {
				setLoading(false);
			}
		};

		return (
			<Modal fullwidth maxWidth="medium">
				<ModalTitle>
					<Typography variant="p1">changeAddress</Typography>
				</ModalTitle>
				<ModalContent>
					<S.FormWrapper>
						<Form formMethods={formMethods}>
							<AddressGroupV2
								formFieldName="newAddress"
								variants={['fillFromGoogleMaps']}
								formMode="edit"
								restrictions={{
									country: {
										isNotDifferent: true,
										code: address?.countryName
									}
								}}
								mapZoom={17}
							/>
						</Form>
					</S.FormWrapper>
				</ModalContent>
				<ModalActions>
					<Button
						label="cancel"
						onClick={() => {
							if (isDirty && !isSubmitSuccessful && !isSubmitting) {
								NiceModal.show(ConfirmationModal, {
									yesAction: () => {
										modal.remove();
									}
								});
							} else {
								modal.remove();
							}
						}}
						variant="outline"
					/>
					<Button
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						size="large"
						label="confirm"
						loading={loading}
					/>
				</ModalActions>
			</Modal>
		);
	}
);

export default BulletConfirmation;
